///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable, NgZone } from '@angular/core';
import { GtthHistoryService } from './history-service';
import { GtthPosition } from '../entities/position';

import { NodeFeature } from '../entities/node/node-feature';
import { GtthStepGroupEnum, PositionDTO } from '../../../generated/shared-types.model';
import {
    GeoJSONStoreFeatures,
    HexColor,
    TerraDraw,
    TerraDrawCircleMode,
    TerraDrawPolygonMode,
    TerraDrawRectangleMode,
    TerraDrawSelectMode
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';

type SelectAreaShape =
    | { type: 'rectangle'; bounds: google.maps.LatLngBounds }
    | { type: 'circle'; center: google.maps.LatLng; radius: number }
    | { type: 'polygon'; path: google.maps.LatLng[] };

export enum GtthSelectAreaModeEnum {
    SQUARE_SELECTOR,
    CIRCLE_SELECTOR,
    POLIGONE_SELECTOR
}

const SELECT_MODE_NAME = 'select';

@Injectable({
    providedIn: 'root'
})
export class GtthSquareSelectionService {
    private terraDraw: TerraDraw;
    private terraDrawAdapter: TerraDrawGoogleMapsAdapter;
    private _historyService: GtthHistoryService;
    private mapContextMenuListener: (event: MouseEvent) => void;
    private mapContextMenuElement: HTMLElement;

    private _overlayType: string;
    private _overlay: SelectAreaShape;
    private readonly selectAreaColor = '#4a90e2';
    private _selectAreaMode: GtthSelectAreaModeEnum = GtthSelectAreaModeEnum.SQUARE_SELECTOR;

    get overlay(): any {
        return this._overlay;
    }

    get selectAreaMode(): GtthSelectAreaModeEnum {
        return this._selectAreaMode;
    }

    set selectAreaMode(value: GtthSelectAreaModeEnum) {
        this._selectAreaMode = value || GtthSelectAreaModeEnum.SQUARE_SELECTOR;
        this.areaModeChanged();
    }

    set historyService(value: GtthHistoryService) {
        this._historyService = value;
    }

    constructor(private readonly ngZone: NgZone) {}

    initDrawSquareListeners() {
        if (!this.terraDraw) {
            this.terraDrawAdapter = new TerraDrawGoogleMapsAdapter({
                lib: google.maps,
                map: this._historyService.map
            });
            this.terraDraw = new TerraDraw({
                adapter: this.terraDrawAdapter,
                modes: [
                    new TerraDrawRectangleMode({
                        styles: this.getAreaStyles(),
                        pointerEvents: this.getPointerEvents(),
                        drawInteraction: 'click-move-or-drag'
                    }),
                    new TerraDrawCircleMode({
                        styles: this.getAreaStyles(),
                        pointerEvents: this.getPointerEvents(),
                        drawInteraction: 'click-move-or-drag'
                    }),
                    new TerraDrawPolygonMode({ styles: this.getAreaStyles(), pointerEvents: this.getPointerEvents() }),
                    new TerraDrawSelectMode({
                        pointerEvents: this.getPointerEvents(),
                        cursors: { pointerOver: 'grab' },
                        flags: {
                            rectangle: {
                                feature: {
                                    draggable: true,
                                    coordinates: { resizable: 'opposite' }
                                }
                            },
                            circle: {
                                feature: {
                                    draggable: true,
                                    // Use a uniform scale anchored at the circle's center so resizing keeps the
                                    // shape circular instead of stretching it into an ellipse (the default
                                    // 'opposite' resize scales the x/y axes independently).
                                    coordinates: { resizable: 'center-fixed' }
                                }
                            },
                            polygon: {
                                feature: {
                                    draggable: true,
                                    coordinates: { draggable: true, midpoints: true, deletable: true }
                                }
                            }
                        }
                    })
                ]
            });
            this.terraDraw.start();
            this.terraDraw.on('change', () => this.updateCursorForDrawingState());
            this.terraDraw.on('finish', (featureId: string | number, context: { action: string; mode: string }) =>
                this.ngZone.run(() => this.onFeatureFinished(featureId, context))
            );
        }

        this._overlayType = this.getDrawingMode();
        this.terraDraw.setMode(this.getDrawingMode());
        this.registerMapRightClickListener();
    }

    private onFeatureFinished(featureId: string | number, context: { action: string; mode: string }) {
        this.updateOverlayFromSnapshot(featureId);
        this._historyService.logicalTopology.clearSelection();
        this.selectEntities();

        if (context?.action === 'draw') {
            this.finalizeDrawnFeature(featureId);
        }
    }

    private finalizeDrawnFeature(featureId: string | number) {
        this.pruneDrawnFeatures(featureId);
        this.terraDrawAdapter.setCursor('grab');
        // Defer selecting the just-finished feature until the drawing mode has fully
        // finished handling the click that triggered this event, otherwise switching
        // to select mode mid-callback can leave the feature unselected/undraggable.
        setTimeout(() => this.selectFinishedFeature(featureId));
    }

    private selectFinishedFeature(featureId: string | number) {
        this.ngZone.run(() => {
            // The feature can have been removed in the meantime (e.g. the user started drawing
            // another shape before this deferred callback ran, pruning this one), so guard against
            // terra-draw throwing when asked to select an id that no longer exists.
            if (this.terraDraw?.hasFeature(featureId)) {
                this.terraDraw.selectFeature(featureId, SELECT_MODE_NAME);
            }
        });
    }

    private pruneDrawnFeatures(keepFeatureId?: string | number) {
        if (!this.terraDraw) {
            return;
        }

        const drawModes = new Set(['rectangle', 'circle', 'polygon']);
        const idsToRemove = this.terraDraw
            .getSnapshot()
            .filter((feature) => drawModes.has(feature.properties?.mode as string))
            .map((feature) => feature.id)
            .filter((id) => id !== undefined && id !== keepFeatureId);

        if (idsToRemove.length) {
            this.terraDraw.removeFeatures(idsToRemove);
        }
    }

    private updateCursorForDrawingState() {
        if (!this.terraDraw || !this.terraDrawAdapter) {
            return;
        }

        const isDrawing = this.terraDraw.getSnapshot().some((feature) => feature.properties?.currentlyDrawing === true);
        if (isDrawing) {
            this.terraDrawAdapter.setCursor('crosshair');
        }
    }

    private registerMapRightClickListener() {
        if (this.mapContextMenuListener || !this._historyService.map) {
            return;
        }

        this.mapContextMenuElement = this._historyService.map.getDiv();
        this.mapContextMenuListener = (event: MouseEvent) => {
            event.preventDefault();

            const coordinates = this.terraDrawAdapter?.getLngLatFromEvent(event);
            if (!coordinates) {
                return;
            }

            this.ngZone.run(() => {
                this.showCommandsWindow(coordinates.lat, coordinates.lng);
            });
        };
        this.mapContextMenuElement.addEventListener('contextmenu', this.mapContextMenuListener);
    }

    private getPointerEvents() {
        return {
            leftClick: true,
            rightClick: false,
            contextMenu: false,
            onDragStart: true,
            onDrag: true,
            onDragEnd: true
        };
    }

    createSelectArea(activeSelectArea: PositionDTO[], selectEntities = true) {
        if (!this._historyService.map) {
            return;
        }

        this.destroyOverlay(false);
        this._overlayType = 'polygon';
        this._overlay = {
            type: 'polygon',
            path: activeSelectArea.map((point) => new google.maps.LatLng(point.latitude, point.longitude))
        };

        if (selectEntities) {
            this._historyService.logicalTopology.clearSelection();
            this.selectEntities();
        }
    }

    areaModeChanged() {
        if (this.terraDraw) {
            this.destroyOverlay();
            this.pruneDrawnFeatures();
            this._historyService.logicalTopology.clearSelection();
            this.terraDraw.setMode(this.getDrawingMode());
        }
    }

    private getDrawingMode(): string {
        switch (this._selectAreaMode) {
            case GtthSelectAreaModeEnum.CIRCLE_SELECTOR:
                return 'circle';
            case GtthSelectAreaModeEnum.POLIGONE_SELECTOR:
                return 'polygon';
            case GtthSelectAreaModeEnum.SQUARE_SELECTOR:
            default:
                return 'rectangle';
        }
    }

    destroyOverlay(hideInfoWindow = true) {
        if (this._overlay) {
            this._overlayType = '';
            this._overlay = undefined;
            this.pruneDrawnFeatures();
            if (hideInfoWindow) {
                this._historyService.infoWindowsService.closeInfoWindow();
            }
        }
    }

    destroyListeners() {
        if (this.terraDraw) {
            this.terraDraw.stop();
            this.destroyOverlay();
            this.terraDraw = undefined;
        }

        if (this.terraDrawAdapter) {
            this.terraDrawAdapter.setCursor('unset');
            this.terraDrawAdapter = undefined;
        }

        if (this.mapContextMenuListener && this.mapContextMenuElement) {
            this.mapContextMenuElement.removeEventListener('contextmenu', this.mapContextMenuListener);
            this.mapContextMenuListener = undefined;
            this.mapContextMenuElement = undefined;
        }
    }

    showCommandsWindow(latitude: number, longitude: number) {
        const infoWindowPosition = new GtthPosition();
        infoWindowPosition.latitude = latitude;
        infoWindowPosition.longitude = longitude;
        this._historyService.infoWindowsService.openSelectAreaStationInfoWindow(infoWindowPosition);
    }

    selectEntities() {
        let selectedStations = [];

        if (!this._overlay) {
            return;
        }

        if (this._overlay.type === 'rectangle') {
            selectedStations = this.getStationsInLatLngBound(this._overlay.bounds);
        } else if (this._overlay.type === 'circle') {
            selectedStations = this.getStationsInCircle(this._overlay.center, this._overlay.radius);
        } else {
            selectedStations = this.getStationsInPolygon(this._overlay.path);
        }

        const potentiallySelectedStations = new Set();

        selectedStations
            .filter((station: NodeFeature) => {
                potentiallySelectedStations.add(station.getId());
                return station.isInActiveLayer();
            })
            .forEach((station: NodeFeature) => this._historyService.logicalTopology.addSelectedStation(station));

        const linksIterator = this._historyService.logicalTopology.getLinksIterator();
        for (const link of linksIterator) {
            if (
                (this._historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES ||
                    link.isUserForced() ||
                    (this._historyService.logicalTopology.isShowPlanningMode && link.isActive())) &&
                potentiallySelectedStations.has(link.getFromId()) &&
                potentiallySelectedStations.has(link.getToId()) &&
                link.isInActiveLayer()
            ) {
                this._historyService.logicalTopology.addSelectedLink(link);
            }
        }
    }

    private getStationsInPolygon(path: google.maps.LatLng[]): NodeFeature[] {
        const stations = [];
        const polygon = new google.maps.Polygon({ paths: path });

        const stationsIterator = this._historyService.logicalTopology.getStationsIterator();
        for (const station of stationsIterator) {
            const googleLatLng = new google.maps.LatLng(station.getPosition().latitude, station.getPosition().longitude);
            if (google.maps.geometry.poly.containsLocation(googleLatLng, polygon)) {
                stations.push(station);
            }
        }

        return stations;
    }

    private getStationsInCircle(center: any, radius: number): NodeFeature[] {
        const stations = [];

        const stationsIterator = this._historyService.logicalTopology.getStationsIterator();
        for (const station of stationsIterator) {
            const googleLatLng = new google.maps.LatLng(station.getPosition().latitude, station.getPosition().longitude);
            const distance = google.maps.geometry.spherical.computeDistanceBetween(center, googleLatLng);
            if (distance <= radius) {
                stations.push({ id: station, distance });
            }
        }
        stations.sort((a, b) => a.distance - b.distance);
        return stations.map((value) => value.id);
    }

    private getAreaStyles() {
        return {
            fillColor: this.selectAreaColor as HexColor,
            fillOpacity: 0.3,
            outlineColor: this.selectAreaColor as HexColor,
            outlineOpacity: 1,
            outlineWidth: 2
        };
    }

    private getStationsInLatLngBound(bounds: any): NodeFeature[] {
        const stations = [];

        const stationsIterator = this._historyService.logicalTopology.getStationsIterator();
        for (const station of stationsIterator) {
            const googleLatLng = new google.maps.LatLng(station.getPosition().latitude, station.getPosition().longitude);
            if (bounds.contains(googleLatLng)) {
                stations.push(station);
            }
        }

        return stations;
    }

    getSelectedAreaPath(): GtthPosition[] {
        if (!this._overlay) {
            return [];
        }

        if (this._overlay.type === 'rectangle') {
            const bounds = this._overlay.bounds;

            const lonMin = bounds.getNorthEast().lng();
            const latMin = bounds.getNorthEast().lat();
            const lonMax = bounds.getSouthWest().lng();
            const latMax = bounds.getSouthWest().lat();

            return [
                GtthPosition.fromValues(lonMin, latMin),
                GtthPosition.fromValues(lonMin, latMax),
                GtthPosition.fromValues(lonMax, latMax),
                GtthPosition.fromValues(lonMax, latMin)
            ];
        } else if (this._overlay.type === 'circle') {
            const pointsCount = 36;
            const circlePath = [];
            for (let i = 0; i < pointsCount; i++) {
                circlePath.push(
                    google.maps.geometry.spherical.computeOffset(this._overlay.center, this._overlay.radius, (i * 360) / pointsCount)
                );
            }
            return circlePath.map((value) => GtthPosition.fromValues(value.lng(), value.lat()));
        } else if (this._overlay.type === 'polygon') {
            return this._overlay.path.map((latLng) => GtthPosition.fromValues(latLng.lng(), latLng.lat()));
        } else {
            return [];
        }
    }

    private updateOverlayFromSnapshot(featureId?: string | number) {
        const snapshot = this.terraDraw?.getSnapshot();
        if (!snapshot?.length) {
            this._overlay = undefined;
            return;
        }

        const feature = (featureId !== undefined && snapshot.find((item) => item.id === featureId)) || snapshot.at(-1);
        this._overlay = this.toSelectAreaShape(feature);
        this._overlayType = this._overlay?.type ?? '';
    }

    private toSelectAreaShape(feature: GeoJSONStoreFeatures): SelectAreaShape | undefined {
        if (feature.geometry.type !== 'Polygon') {
            return undefined;
        }

        const ring = feature.geometry.coordinates[0];
        if (!ring?.length) {
            return undefined;
        }

        const path = ring.slice(0, -1).map(([lng, lat]) => new google.maps.LatLng(lat, lng));
        const mode = feature.properties.mode;

        if (mode === 'rectangle') {
            const bounds = new google.maps.LatLngBounds();
            path.forEach((point) => bounds.extend(point));
            return { type: 'rectangle', bounds };
        }

        if (mode === 'circle') {
            const bounds = new google.maps.LatLngBounds();
            path.forEach((point) => bounds.extend(point));
            const center = bounds.getCenter();
            const radius = google.maps.geometry.spherical.computeDistanceBetween(center, path[0]);
            return { type: 'circle', center, radius };
        }

        return { type: 'polygon', path };
    }
}
